import React, { useCallback, useEffect, useState } from 'react';
import Form from 'js/components/common/Form';
import { pick, prop } from 'ramda';
import { Button } from 'js/components/common/Button';
import { getPromoCodeFromStore } from 'js/utils/promocode.util';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';
import * as hooks from 'js/components/main/QuickDeposit/Deposit/components/CashierForm/hooks';
import { Suggestions } from 'js/components/main/QuickDeposit/Deposit/components/Suggestions';
import { useMethodSelector } from 'js/components/main/QuickDeposit/withCashierPortlet';
import style from './CashierForm.scss';
import { Icon } from 'js/components/common/Icon';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';
import { SwissCashierInput } from '../../../../../common/Fields/SwissInput/SwissCashierInput';
import { useNavigate } from 'js/hooks/navigate';
import { dispatcher } from 'dispatcher';
import { I18n } from 'js/components/common/I18n';
import { CashierFieldAmount } from '../CashierForm/CashierFieldAmount';
import { memoizeOne } from 'js/utils/memoize.util';

export const getSuggestionsOnBlur = (change, blur) =>
    memoizeOne((value) => {
        change('amount', value.target.value.replace(/\D+/g, ''));
        blur('amount');
    });

export const UNLIMITED_MAX = 999999999;
export const UNLIMITED_MAX_VALUE = 999999;

export const CashierForm = ({ deposit }) => {
    const {
        accountFields = [],
        accounts,
        name: nameAcc,
        limits,
        validationConfig,
        defaultSuggestion,
        suggestions,
        amountRequired
    } = useMethodSelector(pick([
        'accountFields',
        'accounts',
        'name',
        'limits',
        'validationConfig',
        'defaultSuggestion',
        'suggestions',
        'amountRequired'
    ]));
    const [account] = usePortletSelector(prop('accounts'));
    const formConfig = hooks.useFormConfig({ accountFields, accounts, validationConfig, defaultSuggestion });
    const initialValues = Object.assign(formConfig.initialValues, { promoCode: getPromoCodeFromStore() });
    const t = usePortletTranslations();
    const depositPage = useAppSelector(prop('depositPage'));
    const formatter = useFormatter();
    const currencyCode = useUserSelector(prop(['currencyCode']));
    const isCardConfirmation = prop('storeCardConfirmationRequired', account);
    const navigate = useNavigate();
    const currency = formatter.getCurrencySymbol();
    const [error, setError] = useState(false);
    const [cashierAmountValue, setCashierAmountValue] = useState(defaultSuggestion);
    const subMethodCode = account?.subMethods?.subMethod[0] && account?.subMethods?.subMethod[0]?.code;
    const [isSuggestionHighlighted, setIsSuggestionHighlighted] = useState(Boolean(defaultSuggestion));

    const goToDeposit = useCallback(async () => {
        navigate(depositPage);
        const isApp = window.document.getElementById('application');
        if (isApp.classList.contains('quick')) {
            isApp.classList.remove('quick');
            dispatcher.dispatch('quickDeposit:hide');
        }
    }, [depositPage, navigate]);

    const suggestionClick = useCallback((value, change, blur) => {
        change('amount', value);
        blur('amount');
        setCashierAmountValue(value);
        setIsSuggestionHighlighted(true);
    }, [setIsSuggestionHighlighted]);

    const onAmountChange = useCallback(() => setIsSuggestionHighlighted(false), [setIsSuggestionHighlighted]);

    useEffect(() => {
        setCashierAmountValue(cashierAmountValue);
        document.querySelector('input#cashierAmount').value = cashierAmountValue;
    }, [cashierAmountValue]);

    return <Form
        onSubmit={deposit}
        initialValues={initialValues}
        validationConfig={formConfig.validationConfig}
        fieldsToReplaceComma={['amount']}
        render={({ submitting, submitError, handleSubmit, values, form: { change, blur } }) => (
            <form
                className={style.form}
                onSubmit={event => event.preventDefault()}
            >
                {submitError && (
                    <ErrorMessage
                        error={submitError}
                        animate={true}
                        className={style.cashierError}
                    />
                )}
                <span className={style.label}>
                    {t('minimum')} {formatter.formatCurrency(limits?.min, { currencyCode, precision: 0 })}
                </span>

                {amountRequired && <div onBlur={getSuggestionsOnBlur(change, blur)}>
                    <CashierFieldAmount
                        type="tel"
                        currency={currency}
                        inputMode="decimal"
                        placeholder=''
                        suggestions={suggestions}
                        defaultValue={amountRequired}
                        submitting={submitting}
                        defaultSuggestion={defaultSuggestion}
                        validationConfig={validationConfig.amount}
                        error={error}
                        setError={setError}
                        cashierAmountValue={cashierAmountValue}
                    />
                </div>
                }
                {amountRequired && <SwissCashierInput
                    type="number"
                    name="amount"
                    readOnly={true}
                    inputMode="decimal"
                    className={style.hiddenCashierAmount}
                    placeholder=''
                    noInput={true}
                    id='cashierAmount'
                    setError={setError}
                    disabled={true}
                    onChange={onAmountChange}
                />}

                <Suggestions
                    onClick={e => suggestionClick(e, change, blur)}
                    suggestions={suggestions}
                    amount={Number(values.amount)}
                    isSuggestionHighlighted={isSuggestionHighlighted}
                />

                <div className={style.label}>
                    {t('payment.method')}
                </div>

                <div className={style.methodContainer}>
                    <div className={style.method}>
                        <div className={style.paymentContainer}><Icon className={`${style.methodIcon} ${style[subMethodCode || nameAcc]}`}></Icon></div>
                        <div className={style.methodInfo}>
                            <I18n className={style.methodName} tKey={`playtech.mobile.system.mobile-cashier.method.${subMethodCode || nameAcc}.title`}/>
                            <span className={style.methodLabel}>
                                {t('maximum')} {limits?.max === UNLIMITED_MAX ?
                                    formatter.formatCurrency(UNLIMITED_MAX_VALUE, { currencyCode, precision: 0, thSeparator: '\'' }) :
                                formatter.formatCurrency(limits?.max, { currencyCode, precision: 0, thSeparator: '\'' })}
                            </span>
                        </div>
                    </div>
                    <div className={style.checkContainer}><Icon className={style.methodCheck}></Icon></div>
                </div>
                <div onClick={goToDeposit} className={style.methodContainer}>
                    <div className={style.method}>
                    <div className={style.iconContainer}><Icon className={`${style.methodIcon} ${style.otherMethod}`} type="defaultMethod"></Icon></div>
                        <div className={style.methodInfo}>
                            <span className={style.methodName}>{t('other.method')}</span>
                        </div>
                    </div>
                    <Icon className={style.arrowRight} type="arrow"></Icon>
                </div>

                <SwissCashierInput
                    type="text"
                    name="promoCode"
                    labelText={t('promoCode.placeholder')}
                    placeholder={t('promoCode')}
                    noValidate={true}
                    onChange={onAmountChange}
                />

                {isCardConfirmation && (
                    <p className={style.plainText}>
                        {t('card-confirmation')}
                    </p>
                )}

                <div className={style.buttons}>
                    <Button type="button" onClick={handleSubmit} disabled={submitting}>
                        {t('deposit.submit.button')}
                    </Button>
                </div>
            </form>
        )}
    />;
};
